import { Priority, PaginatedResponse, ICreatePriority, IUpdatePriority, IUpdateOwnAssignment, IClaimAssignment, SortOrder, PriorityOverview, AssignmentLoad } from "@/types";
import { apiClient } from "@/lib/apiClient";
import { parsePriority } from "@/services/priorityTransforms";

/**
 * Priority Service
 * Handles priority-related API calls (SHPs, KRs)
 */
export const priorityService = {
    /**
     * Get all priorities with optional filters
     */
    getPriorities: async (params?: {
        search?: string;
        type?: string;
        statuses?: string | string[];
        userPid?: string;
        userPids?: string | string[]; // Support multiple user PIDs
        ownerPids?: string | string[]; // Support multiple owner PIDs
        teamPid?: string;
        pppPids?: string[];
        /** true = candidates only, false = committed only, omitted = all */
        candidate?: boolean;
        sortBy?: string;
        sortOrder?: SortOrder;
        page?: number;
        limit?: number;
    }): Promise<PaginatedResponse<Priority>> => {
        const statusesParam = params?.statuses
            ? (Array.isArray(params.statuses) ? params.statuses : [params.statuses])
                .filter(Boolean).join(',') || undefined
            : undefined;
        
        const userPidsParam = Array.isArray(params?.userPids) && params.userPids.length > 0
            ? params.userPids.join(',')
            : typeof params?.userPids === 'string' ? params.userPids : undefined;

        const ownerPidsParam = Array.isArray(params?.ownerPids) && params.ownerPids.length > 0
            ? params.ownerPids.join(',')
            : typeof params?.ownerPids === 'string' ? params.ownerPids : undefined;

        const queryParams: Record<string, string> = {
            page: (params?.page || 1).toString(),
            limit: (params?.limit || 10).toString(),
        };

        if (params?.search) queryParams.search = params.search;
        if (params?.type && params.type !== "all") queryParams.type = params.type;
        if (statusesParam) queryParams.statuses = statusesParam;
        if (params?.userPid && params.userPid !== "all") queryParams.userPid = params.userPid;
        if (userPidsParam) queryParams.userPids = userPidsParam;
        if (ownerPidsParam) queryParams.ownerPids = ownerPidsParam;
        if (params?.teamPid && params.teamPid !== "all") queryParams.teamPid = params.teamPid;
        if (params?.pppPids?.length) queryParams.pppPids = params.pppPids.join(',');
        if (params?.candidate !== undefined) queryParams.candidate = String(params.candidate);
        if (params?.sortBy) queryParams.sortBy = params.sortBy;
        if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

        const res = await apiClient.get<PaginatedResponse<Priority>>("/api/priorities", {
            params: queryParams,
        });
        return {
            ...res,
            data: res.data.map(parsePriority),
        };
    },

    /**
     * Get a single priority by PID
     */
    getPriority: async (pid: string): Promise<Priority> => {
        const priority = await apiClient.get<Priority>(`/api/priorities/${pid}`);
        return parsePriority(priority);
    },

    /**
     * Create a new priority
     */
    createPriority: async (data: ICreatePriority): Promise<Priority> => {
        const created = await apiClient.post<Priority>("/api/priorities", data);
        return parsePriority(created);
    },

    /**
     * Update a priority
     */
    updatePriority: async (pid: string, data: IUpdatePriority): Promise<Priority> => {
        const updated = await apiClient.put<Priority>(`/api/priorities/${pid}`, data);
        return parsePriority(updated);
    },

    /**
     * Update only the current user's own assignment row (status, IC task, blocker).
     *
     * Task-level `updatePriority` is restricted to the DRI, creator and admins; this is the endpoint any
     * assignee owns. It also replaces the old pattern of resending every assignee's row to change one of
     * them, which was a read-modify-write that could silently revert a colleague's status.
     */
    updateOwnAssignment: async (pid: string, data: IUpdateOwnAssignment): Promise<void> => {
        return apiClient.patch<void>(`/api/priorities/${pid}/assignments/me`, data);
    },

    /**
     * Leave a priority — drop the current user's own assignment row.
     *
     * Self-service: no DRI approval needed. Rejected if the caller is the DRI (transfer it first) or if
     * the row being dropped is their Focus and they have not reported on it this week.
     */
    leaveAssignment: async (pid: string): Promise<void> => {
        return apiClient.delete<void>(`/api/priorities/${pid}/assignments/me`);
    },

    /** Join a deliverable that has no DRI; `asDri: true` claims it. Refused with PRIORITY_ALREADY_HAS_DRI once owned. */
    claimAssignment: async (pid: string, data: IClaimAssignment = {}): Promise<void> => {
        return apiClient.post<void>(`/api/priorities/${pid}/assignments/me`, data);
    },

    /**
     * Delete a priority
     */
    deletePriority: async (pid: string): Promise<void> => {
        return apiClient.delete<void>(`/api/priorities/${pid}`);
    },

    /**
     * Reorder priorities
     */

    /**
     * Swap the current user's focus with one of their queued priorities (atomic on the server):
     * the given queued priority becomes Focus and the previous focus drops back to Queue.
     */
    swapFocus: async (focusPid: string): Promise<void> => {
        return apiClient.post<void>("/api/priorities/swap-focus", { focusPid });
    },

    /**
     * Get priority overview statistics
     */
    getOverview: async (): Promise<PriorityOverview> => {
        return apiClient.get<PriorityOverview>("/api/priorities/overview");
    },

    /**
     * Get per-user Focus/Queue load for the given users (to prevent over-assignment inline).
     * `excludePriorityPid` omits the priority being edited so its own rows are not counted.
     */
    getAssignmentLoad: async (userPids: string[], excludePriorityPid?: string): Promise<AssignmentLoad[]> => {
        const pids = userPids.filter(Boolean);
        if (pids.length === 0) return [];
        const params: Record<string, string> = { userPids: pids.join(",") };
        if (excludePriorityPid) params.excludePriorityPid = excludePriorityPid;
        return apiClient.get<AssignmentLoad[]>("/api/priorities/assignment-load", { params });
    },
};
